import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Stack, Select, MenuItem, FormControl, Button, Divider, IconButton, Tooltip,
  TextField, Chip, Menu, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WorkIcon from '@mui/icons-material/Work';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InboxIcon from '@mui/icons-material/Inbox';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import type { QuoteLineDetail, POLineType, TaxMode } from '../data/types';
import { QUOTE_STATUSES } from '../data/types';
import { BRAND, STATUS_TONE, getStatusTone } from '../theme/tokens';
import {
  BTN_STATUS_GREEN, BTN_STANDARD, BTN_GHOST_DOC, BTN_GHOST_DANGER, BTN_PURPLE, LINK_BLUE,
  PO_STICKY_LIFECYCLE_SX, PO_STICKY_LIFECYCLE_PUSH, PO_STICKY_LIFECYCLE_TRAIL,
} from '../theme/poButtonTokens';
import { StatusChip } from '../components/StatusChip';
import { QuoteEditForm } from '../components/QuoteEditForm';
import { InfoableButton, type ButtonInfo } from '../components/ImportResolutionBadge';
import { AiImportBanner } from '../components/AiImportBanner';
import { AttachFileZone } from '../components/AttachFileZone';
import { SupplierImportPreviewDialog } from '../components/SupplierImportPreviewDialog';
import {
  buildImportLineResolution, buildInitialImportResolutions, applyImportPreviewAction,
  batchImportPreviewAction, countPreviewNewLines,
  resolveImportPreviewInternal, type ImportPreviewDeps, type ParsedImportLine,
} from '../utils/importPreviewWorkflow';
import type { ImportLineState, ImportUserAction } from '../utils/codeMatching';
import {
  LineTableColumnSettings, loadColumnVisibility, saveColumnVisibility,
} from '../components/LineTableColumnSettings';
import { QUOTE_LINE_COLUMN_DEFS } from '../config/salesDocumentLineColumns';

const FI_ORANGE = '#F57C00';
const QUOTE_DOC_MAX_WIDTH = 1280;
const QUOTE_MASTHEAD_EDIT_INSET = 2;
const TEMPLATES = ['Default Template', 'Defect Quote II', 'Service Quote', 'Project Quote'];
const QUOTE_TABS = ['Jobs & inventory', 'History'] as const;

const QUOTE_LINE_COL_IDS = QUOTE_LINE_COLUMN_DEFS.map(c => c.id);

/** Parse a simple CSV export into quote line drafts (header row required). */
function parseQuoteCsv(text: string): Array<Partial<QuoteLineDetail>> {
  const rows = text.split(/\r?\n/).map(r => r.trim()).filter(Boolean);
  if (rows.length < 2) return [];
  const headers = rows[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
  const col = (name: string) => headers.findIndex(h => h.includes(name));
  const iType = col('type');
  const iCode = headers.findIndex(h => h.includes('inventory') || h.includes('code'));
  const iDesc = col('description');
  const iQty = col('qty');
  const iPurchase = col('purchase');
  const iMarkup = col('markup');
  const iSales = col('sales');
  const iDisc = col('disc');
  const iTax = col('tax');
  return rows.slice(1).map(row => {
    const cells = row.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
    const get = (i: number) => (i >= 0 ? cells[i] : '') || '';
    const lineType = (get(iType).toLowerCase() === 'service' ? 'service' : 'product') as POLineType;
    const purchasePrice = parseFloat(get(iPurchase)) || 0;
    const markup = parseFloat(get(iMarkup)) || (lineType === 'service' ? 100 : 45);
    const salesFromCsv = parseFloat(get(iSales));
    const salesPrice = salesFromCsv > 0 ? salesFromCsv : deriveSales(purchasePrice, markup);
    return {
      lineType,
      inventoryCode: get(iCode),
      description: get(iDesc) || get(iCode) || 'Imported line',
      qty: parseFloat(get(iQty)) || 1,
      purchasePrice,
      markup,
      salesPrice,
      discount: parseFloat(get(iDisc)) || 0,
      taxable: get(iTax).toUpperCase() !== 'NO',
    };
  }).filter(l => (l.description || '').trim());
}

const FOOTER_BTN_INFO: Record<
  | 'preview' | 'close' | 'download' | 'email' | 'copy' | 'createJob' | 'newVersion'
  | 'invoicePct' | 'forReview' | 'approveEmail' | 'sms' | 'convertProject' | 'deleteQuote',
  ButtonInfo
> = {
  preview: {
    title: 'Preview',
    description: 'Opens a read-only view of this quote exactly as the customer will see it — letterhead, line items, totals, and terms.',
    example: 'Before emailing Q-1042 to the site manager, Preview confirms the service lines, $4,820 total, and your terms render correctly.',
  },
  close: {
    title: 'Close',
    description: 'Leaves this quote and returns to the Quotes list. Edits commit as you make them.',
    example: 'You\u2019ve checked Q-1042\u2019s margin; Close takes you back to the list to work the next quote.',
  },
  download: {
    title: 'Download',
    description: 'Saves a PDF of this quote to your device for your records or to attach to a manual email.',
    example: 'Download Q-1042 as a PDF and file it in the job folder before the site visit.',
  },
  email: {
    title: 'Email',
    description: 'Opens the email composer with the customer\u2019s address pre-filled and the quote PDF attached.',
    example: 'Email Q-1042 to the facilities manager; they receive the PDF and can approve from their inbox.',
  },
  copy: {
    title: 'Copy',
    description: 'Duplicates this quote into a new document — Invoice, PO, Bill, or another Quote — with a fresh number.',
    example: 'Copy Q-1042 \u2192 Purchase Order, tick Product lines only, and a new PO draft opens with stock lines carried across.',
  },
  createJob: {
    title: 'Create job',
    description: 'Creates a field job from this quote\u2019s lines so your team can schedule and track the work.',
    example: 'Q-1042 for a canopy repair — Create job schedules the fitter and links the quote so accepted lines flow to the job card.',
  },
  newVersion: {
    title: 'New Version',
    description: 'Creates a new revision of this quote while keeping the current version in history.',
    example: 'The customer asked for an extra conduit run. New Version opens Q-1042-R2 as Draft with the prior revision superseded.',
  },
  invoicePct: {
    title: 'Invoice % of Quote',
    description: 'Raises a progress invoice for a percentage of the quoted total — used for staged billing on larger quotes.',
    example: 'Q-1042 is $12,400; Invoice 30% raises a $3,720 progress invoice against the accepted quote.',
  },
  forReview: {
    title: 'For Review',
    description: 'Marks this draft quote ready for internal review before it is sent to the customer.',
    example: 'Q-1042 is complete but needs a manager sign-off. For Review moves it into the review queue.',
  },
  approveEmail: {
    title: 'Approve & email',
    description: 'The lifecycle hero — sends the quote to the customer and advances status to Awaiting Acceptance in one step.',
    example: 'Approve & email on Q-1042 emails the PDF to the customer and sets status to Awaiting Acceptance.',
  },
  sms: {
    title: 'SMS',
    description: 'Sends a short SMS with a link to view the quote — a lightweight nudge when email hasn\u2019t been opened.',
    example: 'Q-1042 was emailed three days ago. SMS sends a one-line reminder with the quote link.',
  },
  convertProject: {
    title: 'Convert to Project Quote',
    description: 'Restructures this quote as a multi-phase project quote with budget sections.',
    example: 'Q-1042 covers three site visits. Convert to Project Quote splits lines into Phase 1 / Phase 2 sections.',
  },
  deleteQuote: {
    title: 'Delete',
    description: 'Permanently removes this draft quote. Only available while the quote is a Draft.',
    example: 'Q-1043 was raised against the wrong customer. Delete removes the draft before anything was sent.',
  },
};

function deriveSales(purchase: number, markup: number): number {
  return Math.round(purchase * (1 + markup / 100) * 100) / 100;
}
function lineAmount(l: QuoteLineDetail): number {
  const base = l.salesPrice * l.qty;
  const disc = l.discount ? (base * l.discount) / 100 : 0;
  return base - disc;
}
function lineCost(l: QuoteLineDetail): number {
  return l.purchasePrice * l.qty;
}

function fmtDate(d?: string): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return d; }
}

const DISCLOSURE_HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 0.75,
  mx: -0.5,
  px: 0.75,
  py: 0.5,
  borderRadius: 1,
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#faf8ff' },
} as const;
const DISCLOSURE_ROW_MB = 0.35;

export const QuotePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    quotes, quoteLineDetails, sites, projects, invoices,
    updateQuote, setQuoteStatus, updateQuoteLineDetails, deleteQuote, createInvoice,
  } = useData();

  const quote = quotes.find(q => q.id === id);

  const [colVisible, setColVisible] = useState<Record<string, boolean>>(() =>
    loadColumnVisibility('quote-line-columns-v1', QUOTE_LINE_COL_IDS));
  const toggleCol = (id: string) => {
    setColVisible(prev => {
      const next = { ...prev, [id]: prev[id] === false };
      saveColumnVisibility('quote-line-columns-v1', next);
      return next;
    });
  };
  const showCol = (id: string) => colVisible[id] !== false;
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
  const [createJobAnchor, setCreateJobAnchor] = useState<null | HTMLElement>(null);
  const [tab, setTab] = useState(0);

  const [detailsExpanded, setDetailsExpandedRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('quote-details-expanded-v1') === '1'; } catch { return false; }
  });
  // v1.37.2 — collapsed by default so the quote opens clean; expand on click.
  const [linesExpanded, setLinesExpandedRaw] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('quote-lines-expanded-v2');
      return v === '1';
    } catch { return false; }
  });
  // v1.40 — Invoices + Versions are inline disclosure sections (no longer tabs),
  // mirroring the inline Payments/Bills pattern on the Invoice page. Default
  // expanded so they read as part of the document, collapsible to stay tidy.
  const [invoicesExpanded, setInvoicesExpandedRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('quote-invoices-expanded-v1') !== '0'; } catch { return true; }
  });
  const [versionsExpanded, setVersionsExpandedRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('quote-versions-expanded-v1') !== '0'; } catch { return true; }
  });
  const [quoteParsing, setQuoteParsing] = useState(false);
  const [quoteImportNotice, setQuoteImportNotice] = useState<string | null>(null);
  const [quoteDocUrl, setQuoteDocUrl] = useState<string | null>(null);
  const [quoteDocFile, setQuoteDocFile] = useState<File | null>(null);
  const csvInputRef = React.useRef<HTMLInputElement>(null);

  // v1.37 — supplier-document import now uses the shared SupplierImportPreviewDialog
  // (same modal as PO + Bill), with the source doc as its collapsible left rail.
  // A quote has no supplier price book, so deps are neutral: every parsed line is
  // reviewed, internal codes are generated on demand, nothing touches a price book.
  const [pendingQuoteImport, setPendingQuoteImport] = useState<null | {
    lines: ParsedImportLine[]; documentType?: string; quoteNumber?: string;
    supplierInvoiceNo?: string; demo?: boolean; demoMessage?: string; fileName?: string;
  }>(null);
  const [quotePreviewResolutions, setQuotePreviewResolutions] = useState<Record<number, ImportLineState>>({});
  const [quoteImportPreviewCollapsed, setQuoteImportPreviewCollapsed] = useState(true);
  const quoteImportDeps = useMemo((): ImportPreviewDeps => ({
    supplierId: '',
    supplierName: 'Imported document',
    products: [],
    bookForSupplier: [],
    lookupSupplierCode: () => undefined,
    addProduct: () => {},
  }), []);

  const toggleDetailsExpanded = () => {
    const next = !detailsExpanded;
    setDetailsExpandedRaw(next);
    try { localStorage.setItem('quote-details-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const toggleLinesExpanded = () => {
    const next = !linesExpanded;
    setLinesExpandedRaw(next);
    try { localStorage.setItem('quote-lines-expanded-v2', next ? '1' : '0'); } catch {}
  };
  const toggleInvoicesExpanded = () => {
    const next = !invoicesExpanded;
    setInvoicesExpandedRaw(next);
    try { localStorage.setItem('quote-invoices-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const toggleVersionsExpanded = () => {
    const next = !versionsExpanded;
    setVersionsExpandedRaw(next);
    try { localStorage.setItem('quote-versions-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const disclosureKeyDown = (toggle: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
  };

  const [lines, setLines] = useState<QuoteLineDetail[]>([]);
  useEffect(() => {
    setLines(quoteLineDetails.filter(l => l.quoteId === id));
  }, [id, quoteLineDetails]);

  const [purchasesTax, setPurchasesTax] = useState<TaxMode>('Exclusive');
  const [salesTax, setSalesTax] = useState<TaxMode>('Exclusive');
  useEffect(() => {
    if (quote) {
      setPurchasesTax(quote.purchasesTax ?? 'Exclusive');
      setSalesTax(quote.salesTax ?? 'Exclusive');
    }
  }, [quote]);

  const totals = useMemo(() => {
    const servicesEx = lines.filter(l => l.lineType === 'service').reduce((s, l) => s + lineAmount(l), 0);
    const productsEx  = lines.filter(l => l.lineType === 'product').reduce((s, l) => s + lineAmount(l), 0);
    const subtotal    = servicesEx + productsEx;
    const tax         = lines.filter(l => l.taxable).reduce((s, l) => s + lineAmount(l) * 0.1, 0);
    const cost        = lines.reduce((s, l) => s + lineCost(l), 0);
    const margin      = subtotal > 0 ? ((subtotal - cost) / subtotal) * 100 : 0;
    const markupPct   = cost > 0 ? ((subtotal - cost) / cost) * 100 : 0;
    return { servicesEx, productsEx, subtotal, tax, total: subtotal + tax, cost, margin, markupPct };
  }, [lines]);

  const linkedInvoices = useMemo(
    () => invoices.filter(inv => inv.linkedQuoteId === id),
    [invoices, id],
  );
  const quoteVersions = useMemo(
    () => quotes.filter(q => q.number === quotes.find(x => x.id === id)?.number).sort((a, b) => b.revision - a.revision),
    [quotes, id],
  );

  if (!quote) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: '#888', mb: 1 }}>Quote not found</Typography>
        <Button onClick={() => navigate('/quotes-list')} sx={{ textTransform: 'none', color: BRAND }}>
          ← Back to Quotes
        </Button>
      </Box>
    );
  }

  const customer = sites.find(s => s.id === quote.customerId);
  const project  = projects.find(p => p.id === quote.projectId);
  const customerName = customer?.businessName ?? quote.customerName ?? '—';

  const commitLines = (next: QuoteLineDetail[]) => {
    setLines(next);
    updateQuoteLineDetails(quote.id, next);
  };

  const updateLine = (idx: number, patch: Partial<QuoteLineDetail>) => {
    commitLines(lines.map((l, i) => {
      if (i !== idx) return l;
      const merged = { ...l, ...patch };
      if ('markup' in patch && merged.purchasePrice > 0) merged.salesPrice = deriveSales(merged.purchasePrice, merged.markup);
      if ('purchasePrice' in patch && !('salesPrice' in patch)) merged.salesPrice = deriveSales(merged.purchasePrice, merged.markup);
      if ('salesPrice' in patch && merged.purchasePrice > 0) merged.markup = Math.round(((merged.salesPrice / merged.purchasePrice) - 1) * 10000) / 100;
      return merged;
    }));
  };

  const addLine = (lineType: POLineType) => {
    commitLines([...lines, {
      quoteId: quote.id, lineType, inventoryCode: '', description: '',
      qty: 1, purchasePrice: 0, markup: lineType === 'service' ? 100 : 45,
      salesPrice: 0, discount: 0, taxable: true,
    }]);
  };
  const removeLine = (idx: number) => commitLines(lines.filter((_, i) => i !== idx));

  const showQuoteImportPreview = (
    parsed: ParsedImportLine[],
    file: File | null,
    meta: { documentType?: string; quoteNumber?: string; supplierInvoiceNo?: string; demo?: boolean; demoMessage?: string },
  ) => {
    if (parsed.length === 0) { setQuoteImportNotice('No line items found in that document.'); return; }
    setQuotePreviewResolutions(buildInitialImportResolutions(parsed, quoteImportDeps));
    setPendingQuoteImport({ lines: parsed, fileName: file?.name, ...meta });
    setQuoteImportPreviewCollapsed(true);
    setQuoteImportNotice(null);
  };

  const setQuotePreviewAction = (idx: number, action: ImportUserAction) => {
    if (!pendingQuoteImport) return;
    setQuotePreviewResolutions(prev =>
      applyImportPreviewAction(pendingQuoteImport.lines, prev, idx, action, quoteImportDeps));
  };
  const batchQuotePreviewAction = (action: 'autoCreate' | 'service') => {
    if (!pendingQuoteImport) return;
    setQuotePreviewResolutions(prev =>
      batchImportPreviewAction(pendingQuoteImport.lines, prev, action, quoteImportDeps));
  };
  const quotePreviewNewCount = pendingQuoteImport
    ? countPreviewNewLines(pendingQuoteImport.lines, quotePreviewResolutions, quoteImportDeps)
    : 0;

  const applyQuoteImport = (
    resolutionsOverride?: Record<number, ImportLineState>,
    codeMode: 'useSupplierCodes' | 'autoCreate' = 'autoCreate',
  ) => {
    if (!pendingQuoteImport) return;
    const res = resolutionsOverride ?? quotePreviewResolutions;
    const mintedInBatch: string[] = [];
    const newLines: QuoteLineDetail[] = pendingQuoteImport.lines.map((l, i) => {
      let r = res[i] ?? buildImportLineResolution(l, quoteImportDeps);
      const sc = (l.supplierCode || '').trim();
      let internalCode = '';
      let lineType: 'product' | 'service' = 'product';
      if (codeMode === 'useSupplierCodes') {
        lineType = r.status === 'service' || r.userAction === 'service' ? 'service' : 'product';
        internalCode = lineType === 'service' ? '' : sc;
      } else {
        if (r.status === 'new' && r.userAction === 'pending') r = { ...r, userAction: 'autoCreate' };
        ({ internalCode, lineType } = resolveImportPreviewInternal(l, r, quoteImportDeps, mintedInBatch));
      }
      const purchasePrice = l.unitPrice ?? 0;
      const markup = lineType === 'service' ? 100 : 45;
      return {
        quoteId: quote!.id,
        lineType,
        inventoryCode: internalCode,
        description: l.description || '',
        qty: l.qty ?? 1,
        purchasePrice,
        markup,
        salesPrice: deriveSales(purchasePrice, markup),
        discount: 0,
        taxable: true,
      };
    }).filter(l => l.description.trim());
    commitLines([...lines, ...newLines]);
    setQuoteImportNotice(`${newLines.length} line${newLines.length !== 1 ? 's' : ''} imported.`);
    setPendingQuoteImport(null);
    setQuotePreviewResolutions({});
    setQuoteDocFile(null);
    setQuoteDocUrl(null);
  };
  const useSupplierCodesAndApplyQuote = () => applyQuoteImport(undefined, 'useSupplierCodes');
  const autoCreateInternalCodesAndApplyQuote = () => applyQuoteImport(undefined, 'autoCreate');
  const discardQuoteImport = () => {
    setPendingQuoteImport(null);
    setQuotePreviewResolutions({});
    setQuoteDocFile(null);
    setQuoteDocUrl(null);
  };

  const parseSupplierQuoteFile = (file: File) => {
    setQuoteDocFile(file);
    if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
      setQuoteDocUrl(URL.createObjectURL(file));
    }
    if (file.name.toLowerCase().endsWith('.csv')) {
      const reader = new FileReader();
      reader.onload = () => {
        const parsed = parseQuoteCsv(String(reader.result ?? ''));
        if (parsed.length === 0) {
          setQuoteImportNotice('Could not parse CSV — check the header row (Description, Qty, Purchase, etc.).');
          return;
        }
        const newLines = parsed.map(p => ({
          quoteId: quote!.id,
          lineType: (p.lineType ?? 'product') as POLineType,
          inventoryCode: p.inventoryCode ?? '',
          description: p.description ?? '',
          qty: p.qty ?? 1,
          purchasePrice: p.purchasePrice ?? 0,
          markup: p.markup ?? 45,
          salesPrice: p.salesPrice ?? 0,
          discount: p.discount ?? 0,
          taxable: p.taxable ?? true,
        }));
        commitLines([...lines, ...newLines]);
        setQuoteImportNotice(`${newLines.length} line${newLines.length !== 1 ? 's' : ''} imported from CSV.`);
      };
      reader.readAsText(file);
      return;
    }
    setQuoteParsing(true);
    setQuoteImportNotice(null);
    const fd = new FormData();
    fd.append('file', file);
    fetch('/api/parse-quote', { method: 'POST', body: fd })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data.error) {
          setQuoteImportNotice(data.friendly ?? data.error ?? 'AI parsing unavailable — try CSV import instead.');
          return;
        }
        showQuoteImportPreview(data.lines ?? [], file, {
          documentType: 'quote',
          quoteNumber: data.quoteNumber,
          supplierInvoiceNo: data.invoiceNo,
        });
      })
      .catch(() => setQuoteImportNotice('Could not reach the parse service — try CSV import instead.'))
      .finally(() => setQuoteParsing(false));
  };

  const handleAttachFile = (file: File) => {
    updateQuote(quote!.id, { attachments: [...(quote!.attachments ?? []), file.name] });
  };

  const handleDeleteQuote = () => {
    const msg = quote!.status === 'Draft'
      ? `Delete ${quote!.id}? This cannot be undone.`
      : `Delete ${quote!.id}? This quote is ${quote!.status}. Deleting is permanent.`;
    if (window.confirm(msg)) {
      deleteQuote(quote!.id);
      navigate('/quotes-list');
    }
  };

  const createdLine = quote.createdBy
    ? `Created by ${quote.createdBy}${quote.createdAt ? `, ${new Date(quote.createdAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}`
    : '';

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Page header (identity only — verbs live in footer) ─────────── */}
      <Box className="no-print" sx={{ maxWidth: QUOTE_DOC_MAX_WIDTH, mx: 'auto', width: '100%', flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotes-list')}
              sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#666',
                '&:hover': { backgroundColor: 'transparent', color: BRAND } }}>
              Back
            </Button>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: FI_ORANGE }}>
              {quote.id}
            </Typography>
            <StatusChip
              status={quote.status}
              onClick={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
              deleteIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
              onDelete={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
              sx={{ height: 26, cursor: 'pointer' }}
            />
            <Tooltip title="Template" arrow>
              <Select
                size="small"
                value={quote.template ?? 'Default Template'}
                onChange={e => updateQuote(quote.id, { template: e.target.value })}
                sx={{ fontSize: '0.72rem', height: 28, minWidth: 160, '& .MuiSelect-select': { py: 0.5 } }}
              >
                {TEMPLATES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: '0.72rem' }}>{t}</MenuItem>)}
              </Select>
            </Tooltip>
          </Stack>
          {createdLine && (
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              {createdLine}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ borderBottom: '1px solid transparent', mb: 1.5 }}>
          {QUOTE_TABS.map((label, i) => (
            <Typography key={label}
              onClick={() => setTab(i)}
              sx={{
                fontSize: '0.8rem', fontWeight: i === tab ? 700 : 600,
                color: i === tab ? FI_ORANGE : '#888', pb: 0.6, cursor: 'pointer',
                borderBottom: i === tab ? `2px solid ${FI_ORANGE}` : '2px solid transparent',
                '&:hover': { color: i === tab ? FI_ORANGE : BRAND },
              }}>
              {label}
            </Typography>
          ))}
        </Stack>
      </Box>

      <Menu
        anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Set status
          </Typography>
        </Box>
        {QUOTE_STATUSES.map(s => {
          const tone = STATUS_TONE[getStatusTone(s)];
          const isCurrent = s === quote.status;
          return (
            <MenuItem key={s} selected={isCurrent}
              onClick={() => { setQuoteStatus(quote.id, s); setStatusAnchor(null); }}
              sx={{ py: 0.75, px: 1.5, ...(isCurrent && { backgroundColor: tone.bg }) }}>
              <StatusChip status={s} dense />
            </MenuItem>
          );
        })}
      </Menu>

      <Paper sx={{ maxWidth: QUOTE_DOC_MAX_WIDTH, mx: 'auto', width: '100%', p: 4,
        border: '1px solid #e0e0e0', backgroundColor: '#fff' }} id="quote-print-area">

        {/* ── Masthead card — purple cascade shell ─────────────────────── */}
        <Box className="no-print po-masthead-card" sx={{
          backgroundColor: '#faf8ff',
          border: '1px solid #e0d5f5',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Box sx={{ p: 2 }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={toggleDetailsExpanded}
              onKeyDown={disclosureKeyDown(toggleDetailsExpanded)}
              sx={{ cursor: 'pointer', borderRadius: 1, mx: -1, px: 1, py: 0.25,
                '&:hover': { backgroundColor: '#f3edfc' } }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: BRAND,
                  textTransform: 'uppercase', letterSpacing: 1 }}>
                  Quote
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: BRAND }}>
                    Edit details
                  </Typography>
                  <ExpandMoreIcon sx={{
                    fontSize: 18, color: BRAND,
                    transform: detailsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease',
                  }} />
                </Stack>
              </Stack>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#2a2a2a', mt: 0.3 }}>
                {customerName}
              </Typography>
              <Stack direction="row" spacing={3.5} mt={1} flexWrap="wrap" useFlexGap>
                {[
                  ['Quote Date', fmtDate(quote.quoteDate)],
                  ['Expiry', fmtDate(quote.expiryDate)],
                  ['Project', project?.name ?? '—'],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#888',
                      textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mt: 0.1 }}>{value}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
          {detailsExpanded && (
            <>
              <Divider sx={{ borderColor: '#e0d5f5' }} />
              <Box sx={{ px: QUOTE_MASTHEAD_EDIT_INSET, pb: QUOTE_MASTHEAD_EDIT_INSET, pt: 1.25 }}>
                <QuoteEditForm quote={quote} />
              </Box>
            </>
          )}
        </Box>

        <Divider className="no-print" sx={{ my: 1.25 }} />

        {/* ── Tab body (v1.37.2 — Invoices / Versions / History back on tabs) ─ */}
        {tab === 0 ? (
          <>
        {/* Import + attach — supplier quote PDF or customer CSV */}
        <Box className="no-print" sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {quoteImportNotice && (
            <Alert severity="info" onClose={() => setQuoteImportNotice(null)} sx={{ fontSize: '0.78rem', py: 0.3 }}>
              {quoteImportNotice}
            </Alert>
          )}
          <AttachFileZone
            onFile={handleAttachFile}
            subtitle="Drag files here · attached for reference, not imported into line items"
          />
          <AiImportBanner
            onFile={parseSupplierQuoteFile}
            parsing={quoteParsing}
            title="Import quote lines"
            subtitle="Drag a supplier quote PDF/image or customer CSV · AI reads supplier quotes; CSV imports structured lines"
            parsingLabel="Reading the document…"
            accept="application/pdf,image/*,.csv,text/csv"
            buttonLabel="Choose file"
          />
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" hidden
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) parseSupplierQuoteFile(f);
              if (e.target) e.target.value = '';
            }} />
          {(quote.attachments ?? []).length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {quote.attachments!.map((name, i) => (
                <Chip key={i} label={name} size="small"
                  onDelete={() => updateQuote(quote.id, {
                    attachments: (quote.attachments ?? []).filter((_, j) => j !== i),
                  })}
                  sx={{ fontSize: '0.65rem', height: 22 }} />
              ))}
            </Stack>
          )}
        </Box>

        {/* Line Items — cascade disclosure */}
            <Box
              className="no-print"
              role="button"
              tabIndex={0}
              onClick={toggleLinesExpanded}
              onKeyDown={disclosureKeyDown(toggleLinesExpanded)}
              sx={{ ...DISCLOSURE_HEADER_SX, mb: linesExpanded ? 1.5 : DISCLOSURE_ROW_MB }}
            >
              <ExpandMoreIcon sx={{
                fontSize: 20, color: BRAND,
                transform: linesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease',
              }} />
              <Typography variant="body2" fontWeight={800} sx={{ color: BRAND }}>
                Line Items
              </Typography>
              <Chip
                label={lines.length === 0 ? 'None' : `${lines.length} line${lines.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700,
                  backgroundColor: lines.length > 0 ? '#e8f5e9' : '#f5f5f5',
                  color: lines.length > 0 ? '#2e7d32' : '#888' }}
              />
              {!linesExpanded && lines.length > 0 && (
                <Typography variant="caption" sx={{ color: '#888', fontSize: '0.68rem' }}>
                  · <strong style={{ color: BRAND }}>${totals.total.toFixed(2)}</strong> inc GST
                  · <span style={{ color: BRAND, fontWeight: 700 }}>{totals.margin.toFixed(1)}% margin</span>
                </Typography>
              )}
            </Box>

            {linesExpanded && (
              <Box>
                <Box sx={{ px: 0, py: 1.2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
                  backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #f0f0f0', mb: 1.5 }}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Typography sx={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>Markup (%)</Typography>
                    <Chip label={totals.markupPct.toFixed(2)} size="small"
                      sx={{ backgroundColor: '#f3edfc', color: BRAND, fontWeight: 700, fontSize: '0.7rem' }} />
                  </Stack>
                  <Box flex={1} />
                  <TaxSelect label="Purchases are tax" value={purchasesTax}
                    onChange={v => { setPurchasesTax(v); updateQuote(quote.id, { purchasesTax: v }); }} />
                  <TaxSelect label="Sales are tax" value={salesTax}
                    onChange={v => { setSalesTax(v); updateQuote(quote.id, { salesTax: v }); }} />
                </Box>

                {lines.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <InboxIcon sx={{ fontSize: 44, color: '#e0e0e0', mb: 1 }} />
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#aaa', mb: 0.5 }}>No line items yet</Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: '#bbb', mb: 2 }}>Add a product or service line to build the quote.</Typography>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addLine('product')} sx={BTN_STANDARD}>Add product</Button>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addLine('service')} sx={BTN_STANDARD}>Add service</Button>
                    </Stack>
                  </Box>
                ) : (
                  <Table size="small" sx={{ mb: 1.5 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#fafafa' }}>
                        {showCol('type') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Type</TableCell>}
                        {showCol('inventoryCode') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Inventory Code</TableCell>}
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Qty</TableCell>
                        {showCol('purchase') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Purchase</TableCell>}
                        {showCol('markup') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Markup %</TableCell>}
                        {showCol('sales') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Sales</TableCell>}
                        {showCol('discount') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Disc %</TableCell>}
                        {showCol('tax') && <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Tax</TableCell>}
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#555', py: 1 }}>Amount</TableCell>
                        <TableCell sx={{ width: 36 }} />
                        <LineTableColumnSettings
                          columns={QUOTE_LINE_COLUMN_DEFS}
                          visible={colVisible}
                          onToggle={toggleCol}
                          documentKind="quote"
                          templateName={quote?.template}
                          returnTo={quote ? `/quotes/${quote.id}` : undefined}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lines.map((l, idx) => (
                        <TableRow key={idx} hover>
                          {showCol('type') && (
                          <TableCell sx={{ minWidth: 96 }}>
                            <Select value={l.lineType} variant="standard" disableUnderline
                              onChange={e => updateLine(idx, { lineType: e.target.value as POLineType })}
                              sx={{ fontSize: '0.76rem', fontWeight: 600 }}>
                              <MenuItem value="product">Product</MenuItem>
                              <MenuItem value="service">Service</MenuItem>
                            </Select>
                          </TableCell>
                          )}
                          {showCol('inventoryCode') && (
                          <TableCell sx={{ minWidth: 130 }}>
                            <TextField variant="standard" value={l.inventoryCode}
                              onChange={e => updateLine(idx, { inventoryCode: e.target.value })}
                              InputProps={{ disableUnderline: true, sx: { fontSize: '0.76rem' } }} placeholder="—" />
                          </TableCell>
                          )}
                          <TableCell sx={{ minWidth: 220 }}>
                            <TextField variant="standard" fullWidth value={l.description}
                              onChange={e => updateLine(idx, { description: e.target.value })}
                              InputProps={{ disableUnderline: true, sx: { fontSize: '0.78rem' } }} placeholder="Description" />
                          </TableCell>
                          <TableCell sx={{ width: 64 }}><NumCell value={l.qty} onChange={v => updateLine(idx, { qty: v })} /></TableCell>
                          {showCol('purchase') && <TableCell sx={{ width: 80 }}><NumCell value={l.purchasePrice} onChange={v => updateLine(idx, { purchasePrice: v })} decimals /></TableCell>}
                          {showCol('markup') && <TableCell sx={{ width: 72 }}><NumCell value={l.markup} onChange={v => updateLine(idx, { markup: v })} decimals /></TableCell>}
                          {showCol('sales') && <TableCell sx={{ width: 80 }}><NumCell value={l.salesPrice} onChange={v => updateLine(idx, { salesPrice: v })} decimals /></TableCell>}
                          {showCol('discount') && <TableCell sx={{ width: 64 }}><NumCell value={l.discount} onChange={v => updateLine(idx, { discount: v })} decimals /></TableCell>}
                          {showCol('tax') && (
                          <TableCell sx={{ width: 64 }}>
                            <Select value={l.taxable ? 'YES' : 'NO'} variant="standard" disableUnderline
                              onChange={e => updateLine(idx, { taxable: e.target.value === 'YES' })}
                              sx={{ fontSize: '0.72rem', fontWeight: 700, color: l.taxable ? '#2e7d32' : '#999' }}>
                              <MenuItem value="YES">YES</MenuItem>
                              <MenuItem value="NO">NO</MenuItem>
                            </Select>
                          </TableCell>
                          )}
                          <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right' }}>
                            {lineAmount(l).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeLine(idx)}>
                              <DeleteIcon sx={{ fontSize: 14, color: '#ccc' }} />
                            </IconButton>
                          </TableCell>
                          <TableCell className="no-print" sx={{ width: 40 }} />
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                <Box sx={{ p: 1.5, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => addLine('product')} sx={LINK_BLUE}>Add product</Button>
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => addLine('service')} sx={LINK_BLUE}>Add service</Button>
                  <Box flex={1} />
                  <Button size="small" sx={BTN_STANDARD}>+ Section</Button>
                  <Button size="small" sx={BTN_STANDARD} onClick={() => csvInputRef.current?.click()}>Import CSV</Button>
                  <Button size="small" sx={BTN_STANDARD}>Export CSV</Button>
                  <Button size="small" sx={BTN_STANDARD}>Create Project Budget</Button>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
                  <Paper sx={{ p: 2, minWidth: 280, border: '1px solid #e0e0e0' }}>
                    {[
                      ['Total Services (Ex)', totals.servicesEx],
                      ['Total Products (Ex)', totals.productsEx],
                      ['Subtotal', totals.subtotal],
                      ['Tax amount', totals.tax],
                    ].map(([l, v]) => (
                      <Stack key={l as string} direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                        <Typography variant="caption">{(v as number).toFixed(2)}</Typography>
                      </Stack>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={800}>TOTAL</Typography>
                      <Typography variant="body2" fontWeight={800}>{totals.total.toFixed(2)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: BRAND, fontWeight: 700 }}>Margin</Typography>
                      <Typography variant="caption" sx={{ color: BRAND, fontWeight: 700 }}>{totals.margin.toFixed(2)}%</Typography>
                    </Stack>
                  </Paper>
                </Box>
              </Box>
            )}

            {/* ── Invoices — inline disclosure (mirrors Invoice page Payments) ─ */}
            <Divider className="no-print" sx={{ my: 1.25 }} />
            <Box className="no-print">
              <Box
                role="button"
                tabIndex={0}
                onClick={toggleInvoicesExpanded}
                onKeyDown={disclosureKeyDown(toggleInvoicesExpanded)}
                sx={{ ...DISCLOSURE_HEADER_SX, mb: invoicesExpanded ? 1.5 : DISCLOSURE_ROW_MB }}
              >
                <ExpandMoreIcon sx={{
                  fontSize: 20, color: BRAND,
                  transform: invoicesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.15s ease',
                }} />
                <Typography variant="body2" fontWeight={800} sx={{ color: BRAND }}>
                  Invoices
                </Typography>
                <Chip
                  label={linkedInvoices.length === 0 ? 'None' : `${linkedInvoices.length} invoice${linkedInvoices.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700,
                    backgroundColor: linkedInvoices.length > 0 ? '#e8f5e9' : '#f5f5f5',
                    color: linkedInvoices.length > 0 ? '#2e7d32' : '#888' }}
                />
                {!invoicesExpanded && (
                  <Typography variant="caption" sx={{ color: '#888', fontSize: '0.68rem',
                    fontStyle: linkedInvoices.length === 0 ? 'italic' : 'normal' }}>
                    {linkedInvoices.length === 0
                      ? '· No invoices raised from this quote yet'
                      : <>· Invoiced <strong style={{ color: BRAND }}>${linkedInvoices.reduce((s, i) => s + i.total, 0).toFixed(2)}</strong> across {linkedInvoices.length}</>}
                  </Typography>
                )}
              </Box>
              {invoicesExpanded && (
                <Box>
                  <Stack direction="row" spacing={0.8} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
                    <InfoableButton info={FOOTER_BTN_INFO.invoicePct}>
                      <Button size="small" variant="contained" disableElevation
                        onClick={() => alert('Invoice % of quote — backlog (FieldInsight parity stub).')}
                        sx={BTN_PURPLE}>
                        Invoice % of Quote
                      </Button>
                    </InfoableButton>
                    <Button size="small" variant="contained" disableElevation
                      onClick={() => {
                        const invId = createInvoice({
                          customerId: quote.customerId,
                          template: 'Original',
                          linkedQuoteId: quote.id,
                        });
                        navigate(`/invoices/${invId}`);
                      }}
                      sx={BTN_STANDARD}>
                      + Raise invoice
                    </Button>
                  </Stack>
                  {linkedInvoices.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed #e0d5f5', borderRadius: 2, backgroundColor: '#faf8ff' }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#b0a0d8', fontWeight: 600 }}>No invoices raised from this quote yet</Typography>
                    </Box>
                  ) : (
                    <Table size="small" sx={{ '& td, & th': { fontSize: '0.72rem' } }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                          {['Invoice #', 'Date', 'Status', 'Total (inc GST)', 'Paid', 'Owing', ''].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, color: BRAND, fontSize: '0.68rem', py: 0.8 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {linkedInvoices.map(inv => (
                          <TableRow key={inv.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/invoices/${inv.id}`)}>
                            <TableCell sx={{ fontWeight: 700, color: BRAND }}>{inv.id}</TableCell>
                            <TableCell>{fmtDate(inv.invoiceDate)}</TableCell>
                            <TableCell><StatusChip status={inv.status} dense /></TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>${inv.total.toFixed(2)}</TableCell>
                            <TableCell>${inv.paidAmount.toFixed(2)}</TableCell>
                            <TableCell sx={{ color: inv.owingAmount > 0 ? '#b71c1c' : '#2e7d32', fontWeight: 700 }}>
                              ${inv.owingAmount.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
                                onClick={e => { e.stopPropagation(); navigate(`/invoices/${inv.id}`); }}
                                sx={{ textTransform: 'none', fontSize: '0.65rem', borderColor: BRAND, color: BRAND, py: 0.2, px: 0.8, minWidth: 0 }}>
                                Open
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}
            </Box>

            {/* ── Versions — inline disclosure ─────────────────────────────── */}
            <Divider className="no-print" sx={{ my: 1.25 }} />
            <Box className="no-print">
              <Box
                role="button"
                tabIndex={0}
                onClick={toggleVersionsExpanded}
                onKeyDown={disclosureKeyDown(toggleVersionsExpanded)}
                sx={{ ...DISCLOSURE_HEADER_SX, mb: versionsExpanded ? 1.5 : DISCLOSURE_ROW_MB }}
              >
                <ExpandMoreIcon sx={{
                  fontSize: 20, color: BRAND,
                  transform: versionsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.15s ease',
                }} />
                <Typography variant="body2" fontWeight={800} sx={{ color: BRAND }}>
                  Versions
                </Typography>
                <Chip
                  label={`${quoteVersions.length} version${quoteVersions.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700, backgroundColor: '#f5f0ff', color: BRAND }}
                />
                {!versionsExpanded && (
                  <Typography variant="caption" sx={{ color: '#888', fontSize: '0.68rem' }}>
                    · Currently <strong style={{ color: BRAND }}>Rev {quote.revision}</strong>
                  </Typography>
                )}
              </Box>
              {versionsExpanded && (
                <Box>
                  <InfoableButton info={FOOTER_BTN_INFO.newVersion}>
                    <Button size="small" variant="contained" disableElevation
                      onClick={() => alert('New version — backlog (FieldInsight parity stub).')}
                      sx={{ ...BTN_STANDARD, mb: 1.5 }}>
                      New Version
                    </Button>
                  </InfoableButton>
                  <Table size="small" sx={{ '& td, & th': { fontSize: '0.72rem' } }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                        {['Quote', 'Revision', 'Status', 'Date', 'Total', ''].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, color: BRAND, fontSize: '0.68rem', py: 0.8 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quoteVersions.map(v => (
                        <TableRow key={v.id} hover sx={{ cursor: v.id === quote.id ? 'default' : 'pointer', backgroundColor: v.id === quote.id ? '#faf8ff' : undefined }}
                          onClick={() => { if (v.id !== quote.id) navigate(`/quotes/${v.id}`); }}>
                          <TableCell sx={{ fontWeight: 700, color: BRAND }}>{v.number}</TableCell>
                          <TableCell>
                            Rev {v.revision}{v.id === quote.id ? ' (current)' : ''}
                          </TableCell>
                          <TableCell><StatusChip status={v.status} dense /></TableCell>
                          <TableCell>{fmtDate(v.quoteDate)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>${v.total.toFixed(2)}</TableCell>
                          <TableCell>
                            {v.id !== quote.id && (
                              <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
                                onClick={e => { e.stopPropagation(); navigate(`/quotes/${v.id}`); }}
                                sx={{ textTransform: 'none', fontSize: '0.65rem', borderColor: BRAND, color: BRAND, py: 0.2, px: 0.8, minWidth: 0 }}>
                                Open
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>

          </>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed #e8e8ec', borderRadius: 2 }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#aaa' }}>No history entries yet.</Typography>
          </Box>
        )}

        {/* Version lives in app Layout footer only — no mid-page prototype stamp. */}

        {/* ── Bottom action bar — doc tools scroll; lifecycle sticky below ─ */}
        <Box className="no-print" sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
          <Stack direction="row" spacing={0.8} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap>
              <InfoableButton info={FOOTER_BTN_INFO.preview}>
                <Button size="small" disableElevation startIcon={<VisibilityIcon />}
                  onClick={() => alert('Preview quote — backlog')}
                  sx={BTN_GHOST_DOC}>Preview</Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.download}>
                <Button size="small" disableElevation startIcon={<DownloadIcon />}
                  onClick={() => window.print()} sx={BTN_GHOST_DOC}>Download</Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.email}>
                <Button size="small" disableElevation startIcon={<EmailIcon />}
                  onClick={() => alert('Email quote to customer — backlog')}
                  sx={BTN_GHOST_DOC}>Email</Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.sms}>
                <Button size="small" disableElevation onClick={() => alert('SMS quote link — backlog')} sx={BTN_GHOST_DOC}>SMS</Button>
              </InfoableButton>
            </Stack>

            <Box sx={{ width: 24, flexShrink: 0 }} />

            <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap>
              <InfoableButton info={FOOTER_BTN_INFO.copy}>
                <Button size="small" disableElevation startIcon={<ContentCopyIcon />}
                  onClick={() => alert('Copy quote — backlog')} sx={BTN_GHOST_DOC}>Copy</Button>
              </InfoableButton>
              <InfoableButton info={FOOTER_BTN_INFO.createJob}>
                <Button size="small" disableElevation startIcon={<WorkIcon />}
                  endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                  onClick={e => setCreateJobAnchor(e.currentTarget)}
                  sx={BTN_GHOST_DOC}>Create job</Button>
              </InfoableButton>
              <Menu anchorEl={createJobAnchor} open={Boolean(createJobAnchor)} onClose={() => setCreateJobAnchor(null)}>
                <MenuItem onClick={() => { setCreateJobAnchor(null); setQuoteStatus(quote.id, 'Job Created'); alert('Create job from quote — backlog.'); }}>
                  Create job
                </MenuItem>
                <MenuItem onClick={() => { setCreateJobAnchor(null); alert('Create project jobs — backlog.'); }}>
                  Create project jobs
                </MenuItem>
              </Menu>
              <InfoableButton info={FOOTER_BTN_INFO.convertProject}>
                <Button size="small" disableElevation onClick={() => alert('Convert to project quote — backlog')} sx={BTN_GHOST_DOC}>Convert to Project Quote</Button>
              </InfoableButton>
            </Stack>
          </Stack>
        </Box>

        {/* Row 2 — Lifecycle (sticky: Close · primary · Delete) */}
        <Box className="no-print po-sticky-nav" sx={PO_STICKY_LIFECYCLE_SX}>
            <InfoableButton info={FOOTER_BTN_INFO.close}>
              <Button size="small" disableElevation
                onClick={() => navigate('/quotes-list')}
                sx={BTN_STANDARD}>Close</Button>
            </InfoableButton>

            <Box sx={{ width: 24, flexShrink: 0 }} />

            {quote.status === 'Draft' && (
              <>
                <InfoableButton info={FOOTER_BTN_INFO.forReview}>
                  <Button size="small" disableElevation
                    onClick={() => setQuoteStatus(quote.id, 'Follow Up')}
                    sx={BTN_STANDARD}>For Review</Button>
                </InfoableButton>
                <InfoableButton info={FOOTER_BTN_INFO.approveEmail}>
                  <Button size="small" variant="contained" disableElevation
                    onClick={() => { setQuoteStatus(quote.id, 'Awaiting Acceptance'); alert('Quote emailed to customer (mock).'); }}
                    sx={BTN_STATUS_GREEN}>Approve &amp; email</Button>
                </InfoableButton>
              </>
            )}

            {(quote.status === 'Awaiting Acceptance' || quote.status === 'Follow Up') && (
              <InfoableButton info={FOOTER_BTN_INFO.approveEmail}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => { setQuoteStatus(quote.id, 'Quote Accepted'); alert('Quote accepted (mock).'); }}
                  sx={BTN_STATUS_GREEN}>Approve &amp; email</Button>
              </InfoableButton>
            )}

            {quote.status === 'Quote Accepted' && (
              <InfoableButton info={FOOTER_BTN_INFO.createJob}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => setQuoteStatus(quote.id, 'Job Created')}
                  sx={BTN_STATUS_GREEN}>Create job</Button>
              </InfoableButton>
            )}

            <Box sx={PO_STICKY_LIFECYCLE_PUSH} />

            <Box sx={PO_STICKY_LIFECYCLE_TRAIL}>
              <InfoableButton info={FOOTER_BTN_INFO.deleteQuote}>
                <Button size="small" disableElevation startIcon={<DeleteOutlineIcon sx={{ fontSize: 13 }} />}
                  onClick={handleDeleteQuote}
                  sx={BTN_GHOST_DANGER}>Delete</Button>
              </InfoableButton>
            </Box>
        </Box>
      </Paper>

      {/* v1.37 — shared import-preview modal (same control as PO + Bill). The
          source document is the modal's collapsible left rail; supplier-code
          tracking is off because a quote has no supplier price book. */}
      <SupplierImportPreviewDialog
        open={!!pendingQuoteImport}
        lines={pendingQuoteImport?.lines ?? []}
        resolutions={quotePreviewResolutions}
        targetLabel={quote.id}
        targetKind="quote"
        existingLineCount={lines.length}
        trackSupplierCodes={false}
        previewNewCount={quotePreviewNewCount}
        demo={pendingQuoteImport?.demo}
        demoMessage={pendingQuoteImport?.demoMessage}
        documentType={pendingQuoteImport?.documentType}
        quoteNumber={pendingQuoteImport?.quoteNumber}
        supplierInvoiceNo={pendingQuoteImport?.supplierInvoiceNo}
        fileName={quoteDocFile?.name ?? pendingQuoteImport?.fileName}
        fileUrl={quoteDocUrl}
        fileType={quoteDocFile?.type}
        previewCollapsed={quoteImportPreviewCollapsed}
        onTogglePreviewCollapsed={() => setQuoteImportPreviewCollapsed(c => !c)}
        onSetPreviewAction={setQuotePreviewAction}
        onBatchAction={batchQuotePreviewAction}
        onUseSupplierCodes={useSupplierCodesAndApplyQuote}
        onAutoCreateInternalCodes={autoCreateInternalCodesAndApplyQuote}
        onDiscard={discardQuoteImport}
      />
    </Box>
  );
};

const TaxSelect: React.FC<{ label: string; value: TaxMode; onChange: (v: TaxMode) => void }> = ({ label, value, onChange }) => (
  <Stack direction="row" spacing={0.8} alignItems="center">
    <Typography sx={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>{label}</Typography>
    <FormControl size="small">
      <Select value={value} onChange={e => onChange(e.target.value as TaxMode)}
        sx={{ fontSize: '0.76rem', height: 28, minWidth: 110, '& .MuiSelect-select': { py: 0.4, px: 1 } }}>
        <MenuItem value="Exclusive">Exclusive</MenuItem>
        <MenuItem value="Inclusive">Inclusive</MenuItem>
      </Select>
    </FormControl>
  </Stack>
);

const NumCell: React.FC<{ value: number; onChange: (v: number) => void; decimals?: boolean }> = ({ value, onChange, decimals }) => (
  <TextField variant="standard" type="number"
    value={Number.isFinite(value) ? value : 0}
    onChange={e => onChange(parseFloat(e.target.value) || 0)}
    InputProps={{ disableUnderline: true, sx: { fontSize: '0.76rem' } }}
    inputProps={{ step: decimals ? '0.01' : '1', style: { textAlign: 'right' } }}
  />
);

export default QuotePage;
